import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, CardContent, Grid, TextField, Button, Typography, FormControl, InputLabel, Select, MenuItem, CircularProgress, FormHelperText, Autocomplete } from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { RequiredLabel, getFieldErrorSx, getSelectErrorSx, validateForm } from '../../components/common/FormHelpers';
import StructuredAddressFields from '../../components/common/StructuredAddressFields';

// Required fields for job form
const REQUIRED_FIELDS = ['title', 'account_id'];

const normalizeListingVisibility = (v) => {
  const s = String(v || '').toLowerCase();
  return ['public', 'private', 'confidential'].includes(s) ? s : 'private';
};

const JobForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [employeeContacts, setEmployeeContacts] = useState([]);
  const [workAuthOptions, setWorkAuthOptions] = useState([]);
  const [lookupOptions, setLookupOptions] = useState({
    priority: [],
    status: [],
    employment_type: [],
    remote_option: [],
    listing_visibility: []
  });
  const [selectedWorkAuth, setSelectedWorkAuth] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const submitLock = useRef(false);
  const [formData, setFormData] = useState({
    title: '', description_internal: '', description_external: '', priority: 'medium', status: 'active',
    employment_type: 'fulltime', work_authorization: '', location: '', remote_option: 'onsite',
    address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'US',
    bill_rate: '', pay_rate: '', w2_rate: '', c2c_rate: '', rate_1099: '', salary_min: '', salary_max: '',
    positions_available: 1, experience_min: '', experience_max: '', skills_required: '', skills_preferred: '',
    start_date: '', end_date: '', duration_months: '', notes: '', account_id: '',
    owner_contact_ids: [],
    listing_visibility: 'private'
  });

  useEffect(() => {
    fetchAccounts();
    fetchEmployeeContacts();
    fetchWorkAuthorizations();
    fetchJobLookups();
    if (isEdit) fetchJob();
  }, [id]);

  const fetchAccounts = async () => {
    try { const response = await api.get('/accounts?limit=100&status=active'); setAccounts(response.data.data); } catch (error) { console.error(error); }
  };

  const fetchEmployeeContacts = async () => {
    try {
      const response = await api.get('/contacts?contact_type=employee&status=active&limit=500');
      setEmployeeContacts(response.data.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchWorkAuthorizations = async () => {
    try {
      const response = await api.get('/jobs/work-authorizations');
      setWorkAuthOptions(response.data?.data?.options || []);
    } catch (error) {
      console.error(error);
      setWorkAuthOptions([]);
    }
  };

  const fetchJobLookups = async () => {
    try {
      const response = await api.get('/jobs/lookups');
      const options = response.data?.data?.options || {};
      setLookupOptions({
        priority: options.priority || [],
        status: options.status || [],
        employment_type: options.employment_type || [],
        remote_option: options.remote_option || [],
        listing_visibility: options.listing_visibility || []
      });
    } catch (error) {
      console.error(error);
      setLookupOptions({
        priority: [],
        status: [],
        employment_type: [],
        remote_option: [],
        listing_visibility: []
      });
    }
  };

  const fetchJob = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/jobs/${id}`);
      const d = response.data.data;
      const {
        owners,
        client,
        submissions,
        documents,
        submissionStats,
        hiredEmployees,
        creator,
        updater,
        ...rest
      } = d;
      setFormData((prev) => ({
        ...prev,
        ...rest,
        account_id: rest.account_id || client?.id || prev.account_id,
        owner_contact_ids: (owners || []).map((o) => o.id),
        listing_visibility: normalizeListingVisibility(rest.listing_visibility)
      }));
    }
    catch (error) { toast.error('Failed to fetch'); navigate('/jobs'); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  useEffect(() => {
    const all = String(formData.work_authorization || '');
    if (!all) {
      setSelectedWorkAuth([]);
      return;
    }
    const picked = all
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)
      .map((label) => {
        const match = workAuthOptions.find(
          (o) => o.label.toLowerCase() === label.toLowerCase() || o.code.toLowerCase() === label.toLowerCase()
        );
        return match || { code: label.toLowerCase().replace(/\s+/g, '_'), label };
      });
    setSelectedWorkAuth(picked);
  }, [formData.work_authorization, workAuthOptions]);

  const patchFormData = (patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    Object.keys(patch).forEach((name) => {
      if (validationErrors[name]) {
        setValidationErrors((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const { isValid, errors } = validateForm(formData, REQUIRED_FIELDS);
    if (!isValid) {
      setValidationErrors(errors);
      toast.error('Please fill in all required fields marked with *');
      const firstErrorField = REQUIRED_FIELDS.find(field => errors[field]);
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }
    
    if (submitLock.current) return;
    submitLock.current = true;
    setSaving(true);
    try {
      const omit = new Set([
        'submissions', 'documents', 'submissionStats', 'hiredEmployees', 'client', 'owners', 'creator', 'updater',
        'job_code'
      ]);
      const payload = Object.fromEntries(
        Object.entries(formData).filter(([k]) => !omit.has(k))
      );
      payload.work_authorization = selectedWorkAuth.map((o) => o.label).join(', ');
      if (isEdit) await api.put(`/jobs/${id}`, payload);
      else await api.post('/jobs', payload);
      toast.success(`Job ${isEdit ? 'updated' : 'created'} successfully`);
      navigate('/jobs');
    } catch (error) {
      const d = error.response?.data;
      const errs = d?.errors;
      const detail = Array.isArray(errs) && errs.length ? errs.map((e) => e.msg || e.message || String(e)).join(' · ') : null;
      toast.error(detail || d?.message || 'Failed to save');
    }
    finally {
      setSaving(false);
      submitLock.current = false;
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/jobs')}>Back</Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{isEdit ? 'Edit Job' : 'New Job'}</Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Job Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <TextField 
                  fullWidth 
                  label={<RequiredLabel>Job Title</RequiredLabel>} 
                  name="title" 
                  value={formData.title} 
                  onChange={handleChange} 
                  required
                  error={!!validationErrors.title}
                  helperText={validationErrors.title ? 'Job Title is required' : ''}
                  sx={getFieldErrorSx(validationErrors.title)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!!validationErrors.account_id}>
                  <InputLabel><RequiredLabel>Client Account</RequiredLabel></InputLabel>
                  <Select 
                    name="account_id" 
                    value={formData.account_id} 
                    label="Client Account *" 
                    onChange={handleChange}
                    sx={getSelectErrorSx(validationErrors.account_id)}
                  >
                    {accounts.map(acc => <MenuItem key={acc.id} value={acc.id}>{acc.company_name}</MenuItem>)}
                  </Select>
                  {validationErrors.account_id && <FormHelperText>Client Account is required</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={employeeContacts}
                  getOptionLabel={(c) =>
                    `${[c.first_name, c.last_name].filter(Boolean).join(' ').trim() || '—'}${c.email ? ` (${c.email})` : ''}`
                  }
                  value={employeeContacts.filter((c) => (formData.owner_contact_ids || []).includes(c.id))}
                  onChange={(_, selected) => {
                    setFormData((prev) => ({
                      ...prev,
                      owner_contact_ids: selected.map((c) => c.id)
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign to"
                      placeholder="Select employees (contacts) working this requirement"
                      helperText="Multiple assignees allowed. Only contacts with type Employee are listed."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth><InputLabel>Priority</InputLabel><Select name="priority" value={formData.priority} label="Priority" onChange={handleChange}>
                  {(lookupOptions.priority.length ? lookupOptions.priority : [
                    { code: 'critical', label: 'Critical' }, { code: 'high', label: 'High' }, { code: 'medium', label: 'Medium' }, { code: 'low', label: 'Low' }
                  ]).map((o) => <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>)}
                </Select></FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth><InputLabel>Status</InputLabel><Select name="status" value={formData.status} label="Status" onChange={handleChange}>
                  {(lookupOptions.status.length ? lookupOptions.status : [
                    { code: 'active', label: 'Active' }, { code: 'on_hold', label: 'On Hold' }, { code: 'filled', label: 'Filled' }, { code: 'closed', label: 'Closed' }, { code: 'cancelled', label: 'Cancelled' }
                  ])
                    .filter((o) => !['deleted', 'archived'].includes(o.code))
                    .map((o) => <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>)}
                </Select></FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth><InputLabel>Employment Type</InputLabel><Select name="employment_type" value={formData.employment_type} label="Employment Type" onChange={handleChange}>
                  {(lookupOptions.employment_type.length ? lookupOptions.employment_type : [
                    { code: 'fulltime', label: 'Full Time' }, { code: 'contract', label: 'Contract' }, { code: 'contract_to_hire', label: 'Contract to Hire' }, { code: 'parttime', label: 'Part Time' }
                  ]).map((o) => <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>)}
                </Select></FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth><InputLabel>Remote Option</InputLabel><Select name="remote_option" value={formData.remote_option} label="Remote Option" onChange={handleChange}>
                  {(lookupOptions.remote_option.length ? lookupOptions.remote_option : [
                    { code: 'onsite', label: 'Onsite' }, { code: 'remote', label: 'Remote' }, { code: 'hybrid', label: 'Hybrid' }
                  ]).map((o) => <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>)}
                </Select></FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Public listing (anonymous portal)</InputLabel>
                  <Select
                    name="listing_visibility"
                    value={formData.listing_visibility}
                    label="Public listing (anonymous portal)"
                    onChange={handleChange}
                  >
                    {(lookupOptions.listing_visibility.length ? lookupOptions.listing_visibility : [
                      { code: 'private', label: 'Private' },
                      { code: 'public', label: 'Public' },
                      { code: 'confidential', label: 'Confidential' }
                    ]).map((o) => (
                      <MenuItem key={o.code} value={o.code}>
                        {o.code === 'private' && `${o.label} — not shown on the public jobs page`}
                        {o.code === 'public' && `${o.label} — listed when tenant Jobs visibility is Public`}
                        {o.code === 'confidential' && `${o.label} — not shown publicly`}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    The tenant must also set Jobs to Public under Administration → Visibility setup.
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location summary"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g. Remote — US only (optional if address below is filled)"
                  helperText="Shown in job lists when set; otherwise a line is built from the address fields."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={workAuthOptions}
                  value={selectedWorkAuth}
                  onChange={(_, next) => setSelectedWorkAuth(next)}
                  getOptionLabel={(o) => o.label || ''}
                  isOptionEqualToValue={(a, b) => a.code === b.code}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Work Authorization"
                      placeholder="Type and pick multiple values"
                      helperText="Choose one or more values from seed data."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <StructuredAddressFields
                  title="Work site address"
                  values={{
                    country: formData.country,
                    state: formData.state,
                    city: formData.city,
                    zip_code: formData.zip_code,
                    address_line1: formData.address_line1,
                    address_line2: formData.address_line2,
                  }}
                  onPatch={patchFormData}
                />
              </Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Internal Description" name="description_internal" value={formData.description_internal} onChange={handleChange} multiline rows={4} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="External Description" name="description_external" value={formData.description_external} onChange={handleChange} multiline rows={4} /></Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Rates & Compensation</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={2}><TextField fullWidth label="Bill Rate ($/hr)" name="bill_rate" type="number" value={formData.bill_rate} onChange={handleChange} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Pay Rate ($/hr)" name="pay_rate" type="number" value={formData.pay_rate} onChange={handleChange} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="W2 Rate ($/hr)" name="w2_rate" type="number" value={formData.w2_rate} onChange={handleChange} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="C2C Rate ($/hr)" name="c2c_rate" type="number" value={formData.c2c_rate} onChange={handleChange} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="1099 Rate ($/hr)" name="rate_1099" type="number" value={formData.rate_1099} onChange={handleChange} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Positions" name="positions_available" type="number" value={formData.positions_available} onChange={handleChange} /></Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Skills & Requirements</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}><TextField fullWidth label="Required Skills" name="skills_required" value={formData.skills_required} onChange={handleChange} multiline rows={3} placeholder="Enter skills separated by commas" /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Preferred Skills" name="skills_preferred" value={formData.skills_preferred} onChange={handleChange} multiline rows={2} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Notes" name="notes" value={formData.notes} onChange={handleChange} multiline rows={3} /></Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/jobs')}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} disabled={saving}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>{saving ? 'Saving...' : 'Save Job'}</Button>
        </Box>
      </form>
    </Box>
  );
};

export default JobForm;
